import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import PageHelpLink from "../../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../../constants/helpRouteMap";
import {
  getTeamInvites,
  getTeamMembers,
  inviteTeamMember,
  removeTeamMember,
  revokeTeamInvite,
  TeamInvite,
  TeamMember,
  updateTeamMemberRole,
} from "../../../../helpers/team_helper";
import {
  formatInTenantTimezone,
  readDisplayTimezone,
} from "../../../../helpers/tenantTimezone";
import { isOwner } from "../../../../utils/tenantRoles";

const TeamSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const owner = isOwner();
  const tenantTimezone = useMemo(() => readDisplayTimezone(), []);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"editor" | "approver">("editor");
  const [loading, setLoading] = useState(owner);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!owner) return;
    setLoading(true);
    setError(null);
    try {
      const [memberRes, inviteRes] = await Promise.all([
        getTeamMembers(),
        getTeamInvites(),
      ]);
      setMembers(memberRes.data || []);
      setInvites(inviteRes.data || []);
    } catch (e: any) {
      setError(e?.message || t("team.error.load"));
    } finally {
      setLoading(false);
    }
  }, [owner, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const submitInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const res = await inviteTeamMember({ email: email.trim(), role });
      toast.success(res.message || t("team.success.invited"));
      setEmail("");
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("team.error.invite"));
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (member: TeamMember, next: "editor" | "approver") => {
    setBusy(true);
    try {
      const res = await updateTeamMemberRole(member.id, next);
      setMembers((rows) =>
        rows.map((row) => (row.id === member.id ? res.data : row))
      );
      toast.success(res.message || t("team.success.role"));
    } catch (e: any) {
      toast.error(e?.message || t("team.error.action"));
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (member: TeamMember) => {
    if (!window.confirm(t("team.confirm.removeMember", { name: member.name }))) return;
    setBusy(true);
    try {
      const res = await removeTeamMember(member.id);
      setMembers((rows) => rows.filter((row) => row.id !== member.id));
      toast.success(res.message || t("team.success.removed"));
    } catch (e: any) {
      toast.error(e?.message || t("team.error.action"));
    } finally {
      setBusy(false);
    }
  };

  const revokeInvite = async (invite: TeamInvite) => {
    if (!window.confirm(t("team.confirm.revokeInvite", { email: invite.email }))) return;
    setBusy(true);
    try {
      const res = await revokeTeamInvite(invite.id);
      setInvites((rows) => rows.filter((row) => row.id !== invite.id));
      toast.success(res.message || t("team.success.revoked"));
    } catch (e: any) {
      toast.error(e?.message || t("team.error.action"));
    } finally {
      setBusy(false);
    }
  };

  const resendInvite = async (invite: TeamInvite) => {
    if (!window.confirm(t("team.confirm.resendInvite", { email: invite.email }))) {
      return;
    }
    setBusy(true);
    try {
      const res = await inviteTeamMember({
        email: invite.email,
        role: invite.role,
      });
      const renewedInvite = res.data;
      setInvites((rows) =>
        rows.map((row) => (row.id === invite.id ? renewedInvite : row))
      );
      toast.success(
        renewedInvite?.expires_at
          ? t("team.success.resendWithExpiry", {
              when: formatInTenantTimezone(
                renewedInvite.expires_at,
                tenantTimezone,
                i18n.language || "pt-BR"
              ),
            })
          : res.message || t("team.success.resend")
      );
    } catch (e: any) {
      toast.error(e?.message || t("team.error.resend"));
    } finally {
      setBusy(false);
    }
  };

  document.title = t("team.documentTitle");
  const helpHref = helpUrlForRoute("/settings/team", i18n.language);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("team.title")}
          actions={<PageHelpLink href={helpHref} label={t("common.help")} />}
        />
        <ToastContainer closeButton={false} limit={3} />
        {!owner ? (
          <Alert color="warning">{t("permissions.ownerOnly")}</Alert>
        ) : loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : (
          <Row>
            <Col xl={4}>
              <Card>
                <CardHeader><h5 className="card-title mb-0">{t("team.invite.title")}</h5></CardHeader>
                <CardBody>
                  <p className="text-muted">{t("team.invite.help")}</p>
                  <Form onSubmit={submitInvite}>
                    <div className="mb-3">
                      <Label htmlFor="team-email">{t("team.email")}</Label>
                      <Input id="team-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    <div className="mb-3">
                      <Label htmlFor="team-role">{t("team.role")}</Label>
                      <Input id="team-role" type="select" value={role} onChange={(e) => setRole(e.target.value as "editor" | "approver")}>
                        <option value="editor">{t("team.roles.editor")}</option>
                        <option value="approver">{t("team.roles.approver")}</option>
                      </Input>
                    </div>
                    <Button color="primary" disabled={busy}>{busy ? <Spinner size="sm" /> : t("team.invite.submit")}</Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>
            <Col xl={8}>
              {error && <Alert color="danger">{error} <Button color="link" size="sm" onClick={() => void load()}>{t("common.retry")}</Button></Alert>}
              <Card className="mb-3">
                <CardHeader><h5 className="card-title mb-0">{t("team.members.title")}</h5></CardHeader>
                <CardBody className="p-0">
                  {members.length === 0 ? <p className="text-muted text-center p-4 mb-0">{t("team.members.empty")}</p> : (
                    <div className="table-responsive"><Table className="align-middle mb-0">
                      <thead className="table-light"><tr><th>{t("team.person")}</th><th>{t("team.role")}</th><th /></tr></thead>
                      <tbody>{members.map((member) => {
                        const currentRole = member.role || member.roles?.[0] || "editor";
                        const protectedOwner = currentRole === "owner";
                        return <tr key={member.id}><td><strong>{member.name}</strong><div className="text-muted small">{member.email}</div></td><td>
                          {protectedOwner ? <Badge color="primary">{t("team.roles.owner")}</Badge> : <Input bsSize="sm" type="select" disabled={busy} value={currentRole} onChange={(e) => void changeRole(member, e.target.value as "editor" | "approver")}><option value="editor">{t("team.roles.editor")}</option><option value="approver">{t("team.roles.approver")}</option></Input>}
                        </td><td className="text-end">{!protectedOwner && <Button color="soft-danger" size="sm" disabled={busy} onClick={() => void removeMember(member)}>{t("common.remove")}</Button>}</td></tr>;
                      })}</tbody>
                    </Table></div>
                  )}
                </CardBody>
              </Card>
              <Card>
                <CardHeader><h5 className="card-title mb-0">{t("team.invites.title")}</h5></CardHeader>
                <CardBody className="p-0">
                  {invites.length === 0 ? <div className="text-center p-4"><i className="ri-mail-check-line fs-1 text-muted" /><p className="text-muted mb-0">{t("team.invites.empty")}</p></div> : (
                    <div className="table-responsive"><Table className="align-middle mb-0"><tbody>{invites.map((invite) => <tr key={invite.id}><td>{invite.email}<div className="small text-muted">{t(`team.roles.${invite.role}`)}</div><div className="small text-muted">{t("team.invites.validUntil")}: {formatInTenantTimezone(invite.expires_at, tenantTimezone, i18n.language || "pt-BR")}</div><div className="small text-muted">{t("team.invites.renewedHint")}</div></td><td className="text-end"><div className="d-flex justify-content-end gap-2">{/* LAC-156: reenviar + validade */}<Button color="soft-primary" size="sm" disabled={busy} onClick={() => void resendInvite(invite)}>{t("team.invites.resend")}</Button><Button color="soft-danger" size="sm" disabled={busy} onClick={() => void revokeInvite(invite)}>{t("team.invites.revoke")}</Button></div></td></tr>)}</tbody></Table></div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default TeamSettingsPage;
